import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FFF9EF] text-[#282622]">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#C84B32] hover:text-[#A63D28] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#C84B32] to-[#E8913A] bg-clip-text text-transparent">
          Privacy Policy
        </h1>
        <p className="text-[#6B6560] mb-8">Last updated: May 17, 2025</p>

        <div className="space-y-8 text-[#4A4640] leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">1. Introduction</h2>
            <p>
              Welcome to Nakshatra - Vedic Astrology ("we," "our," or "us"). We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and website (collectively, the "Service").
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">2. Information We Collect</h2>
            <h3 className="text-lg font-medium text-[#C84B32] mb-2">Personal Information</h3>
            <p className="mb-3">We may collect the following personal information:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Name and contact information (phone number, email address)</li>
              <li>Date, time, and place of birth (for astrological calculations)</li>
              <li>Profile information for family members (with your consent)</li>
              <li>Payment information (processed securely through Razorpay)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-[#C84B32] mb-2 mt-4">Automatically Collected Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Device information (device type, operating system)</li>
              <li>Usage data (features accessed, time spent)</li>
              <li>Log data (IP address, browser type)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the collected information for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Generating personalized horoscopes and astrological predictions</li>
              <li>Creating Janam Kundli (birth charts) and compatibility reports</li>
              <li>Processing subscription payments</li>
              <li>Sending notifications about astrological events and predictions</li>
              <li>Improving our services and user experience</li>
              <li>Customer support and communication</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">4. Data Storage and Security</h2>
            <p>
              Your data is stored securely using industry-standard encryption. We use MongoDB for data storage with appropriate security measures. Birth details and personal information are encrypted and protected against unauthorized access.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">5. Third-Party Services</h2>
            <p className="mb-3">We use the following third-party services:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Firebase:</strong> For phone authentication</li>
              <li><strong>Razorpay:</strong> For secure payment processing</li>
              <li><strong>AI Services:</strong> For generating astrological insights (no personal data is shared)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">6. Data Sharing</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share data only:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and safety</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">7. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and associated data</li>
              <li>Opt-out of marketing communications</li>
              <li>Export your data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">8. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[#282622] mb-3">10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-[#FCE9D9]/60 rounded-lg border border-[#F5B942]/40">
              <p><strong>Email:</strong> support@nakshatra.net.in</p>
              <p><strong>Website:</strong> https://nakshatra.net.in</p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-[#282622]/10 text-center text-[#6B6560] text-sm">
          <p>© 2025 Nakshatra - Vedic Astrology. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
